// ** React Imports
import { createContext, useMemo, useContext } from 'react'

// ** Config
import authConfig from 'src/configs/auth'
import { txCampaignUrl, sendMailToMailpit, filterUrl, campaignUrl } from 'src/configs/api'

import { AuthContext } from './AuthContext'
import { PromotionalCampaignContext } from './PromotionalCampaignContext'
import { CampaignContext } from './CampaignContext'
import { authStorage } from 'src/utils/authStorage'
import { useAPI } from 'src/utils/api'
import { useToast } from 'src/@core/components/custom-Toast/customToast'
// ** Defaults

const TransactionalCampaignContext = createContext()

const TransactionalCampaignProvider = ({ children }) => {
  const {
    setIsEmailContentModified,
    setIsWhatsappContentModified,
    setIsSmsContentModified,
    setIsRCSContentModified,
    campaignDirection,
    enableEmail,
    enableWhatsapp,
    enableSms,
    enableRCS,
    setCampaignListData,
    setCampaignsCount,
    setArchiveListData,
    setSelectedCampaign,
    selectedCampaign,
    setCampaignname,
    setPreHeader,
    setSelectedTemplateId,
    previousSelectedTemplateId,
    setPreviousSelectedTemplateId,
    setTags,
    setSelectedChannels,
    setPreviousUserChannels,
    setCampaignDate,
    setCampaignSMSData,
    setPreviewSMSData,
    setCampaignWhatsappData,
    setPreviewWhatsappData,
    setCampaignEmailData,
    setPreviewEmailData,
    setCampaignMessageID,
    fromEmail,
    campaignSenderName,
    replyToEmail,
    campaignsubject,
    emailValue,
    setCampaignFilterList,
    setCampaignOtherFilterList,
    setHasTagFilter,
    fetchWhatsappTranslation,
    fetchEmailTranslation,
    fetchRcsTranslation,
    fetchSmsTranslation,
    setIsCampaignSetupModified,
    setCampaignSaveStatus,
    fetchEmailTemplateTranslation,
    fetchWhatsappTemplateTranslation,
    fetchSMSTemplateTranslation,
    fetchRCSTemplateTranslation,
    getTemplate,
    setApiData,
    setActiveChannel
  } = useContext(CampaignContext)

  const { getChannelIdList, updateChannelStates } = useContext(PromotionalCampaignContext)
  authStorage.getStoredToken(authConfig.storageTokenKeyName)
  const api = useAPI()
  const { showSuccess, showError } = useToast()
  const createTXCampaign = async (campaignname, selectedChannels, selectedTemplateId, tags, validationStatus) => {
    try {
      const inputData = {
        name: campaignname,
        campaignChannels: getChannelIdList(selectedChannels),
        template_id: selectedTemplateId,
        tags: tags
      }

      const response = await api.post(`${txCampaignUrl}`, inputData, {
        params: {
          validation: validationStatus
        },
        skipGlobalToast: true
      })

      const selected_campaign = response?.data?.data
      setIsCampaignSetupModified(false)
      localStorage.setItem('selectedCampaignStore', JSON.stringify(selected_campaign))
      setSelectedCampaign(selected_campaign)
      setCampaignname(selected_campaign?.name)
      setSelectedTemplateId(selected_campaign?.template_id)
      setPreviousSelectedTemplateId(selected_campaign?.template_id)
      setPreHeader(selected_campaign?.pre_headers)
      setTags(selected_campaign?.tags)
      setSelectedChannels(selected_campaign?.campaignChannels || [])
      setPreviousUserChannels(selected_campaign?.campaignChannels || [])
      updateChannelStates(selected_campaign?.campaignChannels || [])
      setCampaignDate(selected_campaign?.start_date)
      const fetchTemplate = async () => {
        if (!selected_campaign?.template_id) {return}

        try {
          const responseTemplate = await getTemplate(selected_campaign.template_id)

          const templateChannelDetails = responseTemplate?.channels
          const campaignChannelDetails = selected_campaign?.campaignChannels

          const campaignChannelMap = new Map(campaignChannelDetails.map(ch => [ch.channelId, ch]))

          templateChannelDetails.forEach(templateChannel => {
            const campaignChannel = campaignChannelMap.get(templateChannel.channelId)

            if (!campaignChannel) {return}

            switch (templateChannel.channelName.toLowerCase()) {
              case 'email':
                fetchEmailTemplateTranslation(
                  selected_campaign?.template_id,

                  campaignChannel.channelId // ✅ pass campaign channelId
                )
                break

              case 'whatsapp':
                fetchWhatsappTemplateTranslation(
                  selected_campaign?.template_id,

                  campaignChannel.channelId
                )
                break

              case 'sms':
                fetchSMSTemplateTranslation(
                  selected_campaign?.template_id,

                  campaignChannel.channelId
                )
                break

              case 'rcs':
                fetchRCSTemplateTranslation(
                  selected_campaign?.template_id,

                  campaignChannel.channelId
                )
                break

              default:
                break
            }
          })
        } catch {}
      }

      fetchTemplate()

      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'Campaign saved successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsCampaignSetupModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to save campaign'
        })
      }
    }
  }
  const archiveTransactionalCampaign = async (campaignId, isArchiveValue) => {
    try {
      const inputData = {
        isArchive: isArchiveValue
      }

      const response = await api.put(`${txCampaignUrl}/${campaignId}/archive`, inputData)
      if (response) {
        showSuccess(response?.data?.data || 'Campaign status updated successfully')
      }
    } catch {}
  }
  const validateTXCampaignPayload = async (type, inputPayload) => {
    try {
      const inputData = {
        action: type,
        data: inputPayload
      }

      const response = await api.post(`${txCampaignUrl}/validate-payload`, inputData)
      if (response?.status === 200) {
        showSuccess('Campaign validated successfully')
        return true
      }
      return false
    } catch {}
  }

  const deleteTXCampaign = async campaignId => {
    try {
      const response = await api.delete(`${txCampaignUrl}/${campaignId}`)
      if (response) {
        showSuccess('Campaign deleted successfully')
        // getTXCampaignList()
      }
    } catch {}
  }

  const updateTXCampaignRecordStatus = async (campaignId, recordStatus) => {
    try {
      const inputData = {
        action: recordStatus,
        startDate: null
      }
      const response = await api.put(`${txCampaignUrl}/${campaignId}/status`, inputData)
      if (response) {
        showSuccess('Campaign archived successfully')
        getTXCampaignList()
      }
    } catch {}
  }

  const updateTXCampaign = async (
    campaignId,
    isCampaignChannelsModified,
    updatedDisabledChannels,
    updatedEnabledChannels,
    campaignname,
    selectedChannels,
    previousSelectedTemplateId,
    selectedTemplateId,
    tags,
    validationStatus
  ) => {
    if (isCampaignChannelsModified) {
      if (updatedDisabledChannels.length > 0) {
        await disableTXCampaignChannels(campaignId, updatedDisabledChannels)
      }

      if (updatedEnabledChannels.length > 0) {
        await enableTXCampaignChannels(campaignId, updatedEnabledChannels)
      }
    }
    try {
      const inputData = {
        name: campaignname,
        campaignChannels: getTXCampaignChannelIdList(selectedChannels),
        template_id: selectedTemplateId,
        // previousSelectedTemplateId !== selectedTemplateId && selectedTemplateId !== null ? selectedTemplateId : null,
        tags: tags
      }

      const response = await api.put(`${txCampaignUrl}/${campaignId}`, inputData, {
        params: {
          validation: validationStatus
        },
        skipGlobalToast: true
      })

      const selected_campaign = response?.data?.data

      // Detect whether the template itself changed on this save — if so, the
      // content mapped in from the (new) template needs to be re-fetched so
      // the form/preview reflect the newly selected template instead of the
      // previous one.
      const templateChanged = previousSelectedTemplateId !== selected_campaign?.template_id

      setIsCampaignSetupModified(false)
      localStorage.setItem('selectedCampaignStore', JSON.stringify(selected_campaign))
      setSelectedCampaign(selected_campaign)
      setCampaignname(selected_campaign?.name)
      setPreHeader(selected_campaign?.pre_headers)
      setSelectedTemplateId(selected_campaign?.template_id)
      setPreviousSelectedTemplateId(selected_campaign?.template_id)
      setTags(selected_campaign?.tags)
      setSelectedChannels(selected_campaign?.campaignChannels || [])
      setPreviousUserChannels(selected_campaign?.campaignChannels || [])
      updateChannelStates(selected_campaign?.campaignChannels || [])
      setCampaignDate(selected_campaign?.start_date)

      if (templateChanged && selected_campaign?.template_id) {
        const fetchTemplate = async () => {
          try {
            const responseTemplate = await getTemplate(selected_campaign.template_id)

            const templateChannelDetails = responseTemplate?.channels
            const campaignChannelDetails = selected_campaign?.campaignChannels

            const campaignChannelMap = new Map((campaignChannelDetails || []).map(ch => [ch.channelId, ch]))

            templateChannelDetails?.forEach(templateChannel => {
              const campaignChannel = campaignChannelMap.get(templateChannel.channelId)

              if (!campaignChannel) {return}

              switch (templateChannel.channelName.toLowerCase()) {
                case 'email':
                  fetchEmailTemplateTranslation(selected_campaign?.template_id, campaignChannel.channelId)
                  break

                case 'whatsapp':
                  fetchWhatsappTemplateTranslation(selected_campaign?.template_id, campaignChannel.channelId)
                  break

                case 'sms':
                  fetchSMSTemplateTranslation(selected_campaign?.template_id, campaignChannel.channelId)
                  break

                case 'rcs':
                  fetchRCSTemplateTranslation(selected_campaign?.template_id, campaignChannel.channelId)
                  break

                default:
                  break
              }
            })
          } catch {}
        }

        fetchTemplate()
      }

      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'Campaign updated successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsCampaignSetupModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to update campaign'
        })
      }
    }
  }

  const updateFinalTXCampaign = async (campaignId, campaignStatus, campaignSendDate) => {
    try {
      const inputData = {
        action: campaignStatus,
        startDate: campaignStatus === 'Draft' || !campaignSendDate || campaignSendDate === '' ? null : campaignSendDate
      }
      const response = await api.put(`${campaignUrl}/${campaignId}/status`, inputData)
      if (response?.status === 200) {
        showSuccess('Campaign updated successfully')
        return true
      }
      return false
    } catch {}
  }

  const getTXCampaignList = async payload => {
    try {
      const response = await api.post(`${txCampaignUrl}/search`, payload)
      setCampaignListData(response?.data?.data?.campaigns || [])
      setCampaignsCount(response?.data?.data?.total || 0)
    } catch {}
  }

  const getTransactinalCampaignFilterList = async () => {
    try {
      const response = await api.get(`${filterUrl}/campaigns?type=transactional`)

      const filters = response?.data?.data?.filters || []
      const otherFilters = response?.data?.data?.otherFilters || []

      const tagsFilter = filters.find(f => f.key === 'tags')
      setHasTagFilter(!!tagsFilter)
      setCampaignFilterList(tagsFilter ? tagsFilter.options.map(o => o.value) : [])
      setCampaignOtherFilterList(otherFilters)

      const initialSelectedFilters = {}
      otherFilters.forEach(f => {
        initialSelectedFilters[f.key] = []
      })

      return initialSelectedFilters
    } catch {}
  }

  const getTXCampaignArchiveList = async () => {
    try {
      const response = await api.get(`${txCampaignUrl}?status=archived`)

      setArchiveListData(response?.data?.data)
      showSuccess('Archive list loaded successfully')
    } catch {}
  }
  const updateFinalTxCampaign = async (id, campaignStatus, campaignSendDate, validationStatus, payload) => {
    try {
      // v1/api/c/campaigns/{campaignId}/status
      const response = await api.put(`${campaignUrl}/transactional/${id}/status`, payload, {
        params: {
          validation: validationStatus
        }
      })
      if (response?.status === 200) {
        return true
      }
      return false
    } catch {}
  }

  const getTXCampaign = async id => {
    try {
      const response = await api.get(`${txCampaignUrl}/${id}`)

      const selected_campaign = response?.data?.data
      localStorage.setItem('selectedCampaignStore', JSON.stringify(selected_campaign))
      setSelectedCampaign(selected_campaign)
      setCampaignname(selected_campaign?.name)
      setPreHeader(selected_campaign?.pre_headers)
      setSelectedTemplateId(selected_campaign?.template_id)
      setPreviousSelectedTemplateId(selected_campaign?.template_id)
      setTags(selected_campaign?.tags)
      setSelectedChannels(selected_campaign?.campaignChannels || [])
      setPreviousUserChannels(selected_campaign?.campaignChannels || [])
      updateChannelStates(selected_campaign?.campaignChannels || [])
      setCampaignDate(selected_campaign?.start_date)

      return true
    } catch {
      return false
    }
  }
  const getTXCampaignMessage = async campaignChannelId => {
    try {
      const response = await api.get(`${txCampaignUrl}/${selectedCampaign?.id}/campaignChannels/${campaignChannelId}`)
      const messageData = response?.data?.data
      if (messageData) {
        if (messageData.channelName.toLowerCase() === 'sms') {
          setCampaignSMSData(messageData)
          setPreviewSMSData(messageData)
        } else if (messageData.channelName.toLowerCase() === 'whatsapp') {
          setCampaignWhatsappData(messageData)
          setPreviewWhatsappData(messageData)
        } else if (messageData.channelName.toLowerCase() === 'email') {
          setCampaignEmailData(messageData)
          setPreviewEmailData(messageData)
        } else {
          showError('Invalid channel_id')
        }
      }
    } catch {}
  }

  const updateTXCampaignSMSMessage = async (
    campaignId,
    smsChannelId,
    smsCampaignChannelId,
    smsPayload,
    validationStatus,
    type
  ) => {
    setCampaignSaveStatus(null)
    try {
      const response = await api.put(`${campaignUrl}/${campaignId}/channeldata/${smsChannelId}`, smsPayload, {
        params: {
          validation: false
          // validation: validationStatus
        },
        skipGlobalToast: true
      })
      setIsSmsContentModified(false)

      let channelSmsId = response?.data?.data?.channelId
      fetchSmsTranslation(channelSmsId, type)
      const campaignMessage_id = response?.data?.data.id

      setCampaignMessageID(campaignMessage_id)

      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'SMS Campaign message updates successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsSmsContentModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to update SMS message'
        })
      }
    }
  }
  const updateTXCampaignRCSMessage = async (
    campaignId,
    rcsChannelId,
    rcsCampaignChannelId,
    rcsPayload,
    validationStatus,
    type
  ) => {
    setCampaignSaveStatus(null)
    try {
      const response = await api.put(`${campaignUrl}/${campaignId}/channeldata/${rcsChannelId}`, rcsPayload, {
        params: {
          validation: false
          // validation: validationStatus
        },
        skipGlobalToast: true
      })
      setIsRCSContentModified(false)

      let channelRCSId = response?.data?.data?.channelId
      fetchRcsTranslation(channelRCSId, type)
      const campaignMessage_id = response?.data?.data.id

      setCampaignMessageID(campaignMessage_id)

      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'RCS Campaign message updates successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsRCSContentModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to update RCS message'
        })
      }
    }
  }

  const updateTXCampaignWhatsappMessage = async (
    campaignId,
    whatsappChannelId,
    whatsappCampaignChannelId,
    whatsappPayload,
    validationStatus,
    type
  ) => {
    setCampaignSaveStatus(null)
    try {
      const response = await api.put(`${campaignUrl}/${campaignId}/channeldata/${whatsappChannelId}`, whatsappPayload, {
        params: {
          validation: validationStatus
        },
        skipGlobalToast: true
      })

      setIsWhatsappContentModified(false)
      const channelWhatsappId = response?.data?.data?.channelId
      fetchWhatsappTranslation(channelWhatsappId, type)
      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'Whatsapp Campaign message updates successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsWhatsappContentModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to update Whatsapp message'
        })
      }
    }
  }

  const updateTXCampaignEmailMessage = async (
    campaignId,
    emailChannelId,
    emailCampaignChannelId,
    emailPayload,
    validationStatus,
    type
  ) => {
    setCampaignSaveStatus(null)
    try {
      const response = await api.put(
        `${campaignUrl}/${campaignId}/channeldata/${emailChannelId}`,

        emailPayload,
        {
          params: {
            // validation: validationStatus
            validation: false
          },
          skipGlobalToast: true
        }
      )
      setIsEmailContentModified(false)
      const channelEmailId = response?.data?.data?.channelId
      // idsOnly: local attachments/subject/body already match what was just saved —
      // only sync campaignApiId's IDs here, don't let a re-fetched copy overwrite them.
      await fetchEmailTranslation(channelEmailId, type, true)

      if (response?.data?.data.id) {
        const campaignMessage_id = response?.data?.data.id

        setCampaignMessageID(campaignMessage_id)
      }
      if (response?.status === 200) {
        setCampaignSaveStatus({ type: 'success', message: 'Campaign Email updated successfully' })
        return true
      }
      return false
    } catch (error) {
      if (error) {
        setIsEmailContentModified(true)
        setCampaignSaveStatus({
          type: 'error',
          message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to update campaign email'
        })
      }
    }
  }

  const getEditorList = async () => {
    api
      .get(`${txCampaignUrl}/editors`)
      .then(async _response => {})
      .catch(_err => {})
  }

  const formatToDateString = dateString => {
    if (dateString === null) {
      return ''
    } else {
      const date = new Date(dateString)

      const year = date.getFullYear()

      const month = String(date.getMonth() + 1).padStart(2, '0')

      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')

      const minutes = String(date.getMinutes()).padStart(2, '0')

      const seconds = String(date.getSeconds()).padStart(2, '0')

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }
  }

  const determineDefaultValue = () => {
    if (campaignDirection === 'f') {
      if (enableEmail) {return 'email'}
      if (enableWhatsapp) {return 'whatsapp'}
      if (enableSms) {return 'sms'}
      if (enableRCS) {return 'rcs'}
    } else if (campaignDirection === 'b') {
      if (enableRCS) {return 'rcs'}
      if (enableSms) {return 'sms'}
      if (enableWhatsapp) {return 'whatsapp'}
      if (enableEmail) {return 'email'}
    }
    return ''
  }

  const determineDefaultChannelStep = () => {
    if (campaignDirection === 'f') {
      if (enableEmail) {return 1.1}
      if (enableWhatsapp) {return 1.2}
      if (enableSms) {return 1.3}
      if (enableRCS) {return 1.4}
    } else if (campaignDirection === 'b') {
      if (enableRCS) {return 1.4}
      if (enableSms) {return 1.3}
      if (enableWhatsapp) {return 1.2}
      if (enableEmail) {return 1.1}
    }
    return 0
  }

  function formatDate(timestamp) {
    // Convert to Date object
    const date = new Date(timestamp)

    // Format the date
    const formattedDate = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`

    // Format the time
    const hours = date.getHours()
    const formattedTime = `${hours % 12 || 12}:${date.getMinutes().toString().padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`

    // Combine date and time
    return `${formattedDate} ${formattedTime}`
  }

  const getTXCampaignChannelIdList = selectedChannelsList => {
    return selectedChannelsList
      .filter(channel => channel.channelStatus === 'active')
      .map(channel => ({
        campaignChannelId: channel.campaignChannelId,
        channelId: channel.channelId
      }))
  }

  const enableTXCampaignChannels = async (campaignId, channelIds) => {
    try {
      const queryParams = channelIds.map(id => `channelId=${id}`).join('&')

      const response = await api.put(`${txCampaignUrl}/${campaignId}/campaignChannels?${queryParams}`, {})
      if (response?.status === 200 || response?.status === 204) {
      } else {
        showError('Failed to disable channels')
      }
    } catch {}
  }

  const disableTXCampaignChannels = async (campaignId, campaignChannelIds) => {
    try {
      const queryParams = campaignChannelIds.map(id => `campaignChannelId=${id}`).join('&')

      const response = await api.delete(`${txCampaignUrl}/${campaignId}/campaignChannels?${queryParams}`)

      if (response?.status === 200 || response?.status === 204) {
      } else {
        showError('Failed to disable channels')
      }
    } catch {}
  }

  const getTxCampaignTriggerApiDetails = async campaignId => {
    try {
      const response = await api.get(`${txCampaignUrl}/${campaignId}/trigger-details`)
      if (response?.status === 200 || response?.status === 204) {
        var apiDetails = response?.data?.data
        setApiData(apiDetails)
        if (apiDetails?.campaign?.campaignChannels?.length > 0) {
          const firstChannel = apiDetails.campaign.campaignChannels[0]

          setActiveChannel({
            name: firstChannel.channelName,
            iconName: firstChannel.channelIconName,
            iconColor: firstChannel.channelIconColor
          })
        }
      }
    } catch {}
  }
  const sendCampaignMailToMailpit = async () => {
    try {
      const emailPayload = {
        Attachments: [],
        Bcc: [],
        Cc: [],
        From: {
          Email: fromEmail,
          Name: campaignSenderName
        },
        HTML: emailValue,
        Headers: {},
        ReplyTo: replyToEmail
          ? [
              {
                Email: replyToEmail,
                Name: campaignSenderName
              }
            ]
          : [],
        Subject: campaignsubject,
        Tags: [],
        Text: ''
      }
      const response = await api.post(sendMailToMailpit, emailPayload)
      setMailpitID(response?.data?.id)

      if (response?.status === 200) {
        showSuccess('Email campaign sent successfully')
        return true
      }
      return false
    } catch {}
  }

  const values = useMemo(
    () => ({
      createTXCampaign,
      determineDefaultValue,
      validateTXCampaignPayload,
      determineDefaultChannelStep,
      getTXCampaignMessage,
      getTXCampaignList,
      getTXCampaignArchiveList,
      getTXCampaign,
      updateTXCampaignSMSMessage,
      updateTXCampaignRCSMessage,
      updateTXCampaignWhatsappMessage,
      updateTXCampaignEmailMessage,
      getEditorList,
      updateTXCampaign,
      deleteTXCampaign,
      // cloneTXCampaign,
      updateTXCampaignRecordStatus,
      formatToDateString,
      formatDate,
      updateFinalTXCampaign,
      getTxCampaignTriggerApiDetails,
      sendCampaignMailToMailpit,
      getTransactinalCampaignFilterList,
      updateFinalTxCampaign: updateFinalTxCampaign,
      archiveTransactionalCampaign
    }),
    []
  )

  return <TransactionalCampaignContext.Provider value={values}>{children}</TransactionalCampaignContext.Provider>
}

export { TransactionalCampaignContext, TransactionalCampaignProvider }
